import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SLIDES = [
  { src: '/demo-dashboard.png', label: 'Tableau de bord', route: 'app.devis.go/dashboard' },
  { src: '/demo-devis.png', label: 'Devis', route: 'app.devis.go/devis' },
  { src: '/demo-crm.png', label: 'CRM', route: 'app.devis.go/crm' },
  { src: '/demo-taches.png', label: 'Tâches terrain', route: 'app.devis.go/taches' },
  { src: '/demo-facturation-mensuelle.png', label: 'Facturation mensuelle', route: 'app.devis.go/facturation-mensuelle' },
  { src: '/demo-factures.png', label: 'Factures', route: 'app.devis.go/factures' },
  { src: '/demo-calendrier.png', label: 'Agenda', route: 'app.devis.go/agenda' },
];

const INTERVAL_MS = 3500;

const DemoCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Preload all images so transitions are instant
  useEffect(() => {
    SLIDES.forEach(s => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setIndex(i => (i + 1) % SLIDES.length);
    }, INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  const go = (i) => setIndex((i + SLIDES.length) % SLIDES.length);
  const current = SLIDES[index];

  return (
    <div
      className="relative"
      data-testid="demo-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 to-amber-200/30 rounded-3xl blur-2xl" aria-hidden />
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10 bg-white">
        {/* Browser chrome */}
        <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
          <span
            className="ml-2 text-[11px] text-slate-400 font-mono truncate"
            data-testid="demo-route-label"
          >
            {current.route}
          </span>
        </div>

        {/* Slides stack with fade animation */}
        <div className="relative aspect-[16/10] bg-slate-100">
          {SLIDES.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt={`Aperçu ${s.label} — Devis.Go`}
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-in-out ${
                i === index ? 'opacity-100' : 'opacity-0'
              }`}
              draggable="false"
            />
          ))}

          {/* Label pill */}
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold text-slate-800 border border-slate-200 shadow-sm">
            {current.label}
          </div>

          {/* Arrows */}
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="Précédent"
            data-testid="demo-prev-btn"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-700 transition opacity-0 group-hover:opacity-100"
            style={{ opacity: paused ? 1 : 0 }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="Suivant"
            data-testid="demo-next-btn"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-700 transition"
            style={{ opacity: paused ? 1 : 0 }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-1.5 py-3 bg-white border-t border-slate-100">
          {SLIDES.map((s, i) => (
            <button
              key={s.src}
              type="button"
              onClick={() => go(i)}
              aria-label={`Aller à ${s.label}`}
              data-testid={`demo-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-orange-500' : 'w-1.5 bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemoCarousel;
